/**
 * Feed Service - Brand New Clean Architecture
 *
 * All API calls properly handle paginated responses
 * Clean separation between different feed operations
 * Consistent error handling and logging
 * Proper data transformation using the new mappers
 */

import { joinMeetingRoom } from '../../../socket/namespaces/communication-socket';
import { MESSAGE_TYPE, FEED_TYPE, PAGINATION_CONSTANTS } from '../constants';
import { transformSocketEventToMessage, transformApiFeedToDisplay, mapUnreadCounts } from '../mappers/feedUtility';
import request, { getAnnouncementAudit, getFeatures } from '../../../../util/APIUtils';
import { HERMES_BASE_URL } from '../../../../constants';
import { getActiveLocationId } from '../../../services/utilty';
import { ManagementService } from '../../../../components/management/management.service';

const API_BASE = '/api/feeds';


export const getErrorMsg = (statusCode, additionalInfo = "") => {
  switch (statusCode) {
    case 200:
      return additionalInfo;
    default:
      return "Oops! Our hamsters powering the servers took a coffee break. Something went wrong while fetching the data. Please try again in a moment. \n {additionalInfo}".replace('{additionalInfo}', additionalInfo);
  }
};
/**
 * Get all feeds for socket room joining
 * Uses large page size or unpaged request to get all available feeds
 * Returns basic feed information needed for socket room joining
 */
export const getAllFeeds = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      // Use large page size to get all feeds for socket rooms
      page: 0,
      size: params.maxSize || PAGINATION_CONSTANTS.SOCKET_ROOM_MAX_FEEDS, // Large number to get all feeds
      ...(params.feedType && { feedType: params.feedType }),
      ...(params.locationKeys && { locationKeys: params.locationKeys.join(',') }),
      ...(params.groupKeys && { groupKeys: params.groupKeys.join(',') }),
    });

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      // Handle paginated response structure
      const pageData = response.data;
      const feeds = pageData.content || [];

      return feeds.map((feed) => ({
        id: feed.id,
        name: feed.name,
        isConfidential: feed.isConfidential || false,
      }));
    }

    return [];
  } catch (error) {
    console.error('[FeedService] Failed to get all feeds for socket rooms:', error);
    return [];
  }
};

/**
 * Get paginated feeds with message previews (All Feeds tab)
 * Returns paginated feed objects with message previews for display in All Feeds
 */
export const getFeedsWithMessages = async (page = 1, size = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE, feedType = null) => {
  try {
    const queryParams = new URLSearchParams({
      page: page - 1, // Convert to 0-based pagination
      size: size,
      ...(feedType && { feedType: feedType }),
    });

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/messages?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      const pageData = response.data;

      return {
        data: pageData.content || [],
        totalFeedCount: pageData.totalElements || 0,
        totalPages: pageData.totalPages || 0,
        currentPage: page, // Return 1-based page for UI
        hasMore: !pageData.last,
      };
    }

    return {
      data: [],
      totalFeedCount: 0,
      totalPages: 0,
      currentPage: page,
      hasMore: false,
    };
  } catch (error) {
    console.error('[FeedService] Failed to get feeds with messages:', error);
    return {
      data: [],
      totalFeedCount: 0,
      totalPages: 0,
      currentPage: page,
      hasMore: false,
    };
  }
};

/**
 * Get messages for a specific feed/conversation
 * Returns paginated message data transformed for display
 * API returns paginated structure: BaseResponseDto<Page<FeedMessageResponse>>
 * @param {string} feedId - Feed ID
 * @param {Object} params - Parameters including currentUserKey, menuProps, page, size, personKey
 */
export const getFeedMessages = async (feedId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: (params.page || 1) - 1, // Convert to 0-based pagination
      size: params.size || PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
      includeDeleted: params.includeDeleted || false,
      ...(params.personKey && { personKey: params.personKey }),
    });

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/${feedId}/messages?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      const pageData = response.data;
      const messages = Array.isArray(pageData.content) ? pageData.content : [];
      const transformedMessages = messages.map((msg) => 
        transformSocketEventToMessage(msg, params.currentUserKey, params.menuProps)
      );

      return {
        data: transformedMessages,
        totalMessages: pageData.totalElements || 0,
        totalPages: pageData.totalPages || 0,
        currentPage: params.page || 1, // 1-based for UI
        hasMore: !pageData.last, // last=false means more pages available
      };
    }

    return {
      data: [],
      totalMessages: 0,
      totalPages: 0,
      hasMore: false,
      currentPage: params.page || 1,
    };
  } catch (error) {
    console.error('[FeedService] Failed to get feed messages:', error);
    return {
      data: [],
      totalMessages: 0,
      totalPages: 0,
      hasMore: false,
      currentPage: params.page || 1,
    };
  }
};

/**
 * Get message context for a specific message
 * Returns message data around a specific message for audit purposes
 * API returns List structure: BaseResponseDto<List<FeedMessageResponse>>
 * @param {string} messageId - Message ID to get context for
 * @param {Object} params - Parameters including currentUserKey, menuProps, before, after, includeDeleted, personKey
 */
export const getMessageContext = async (messageId, params = {}) => {
  try {
    // Use nullish coalescing (??) instead of || to allow 0 as a valid value
    // Only include after in query params if it's explicitly provided (not undefined)
    const queryParamsObj = {
      before: params.before ?? 0,
      includeDeleted: params.includeDeleted ?? true
    };
    
    // Only add 'after' if it's explicitly provided (allows 0 as valid value)
    if (params.after !== undefined) {
      queryParamsObj.after = params.after;
    } else {
      // Default to 20 only if after is not provided at all
      queryParamsObj.after = 20;
    }
    
    const queryParams = new URLSearchParams(queryParamsObj);

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/messages/${messageId}/context?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      // API returns BaseResponseDto<List<FeedMessageResponse>>, so data is directly an array
      const messages = Array.isArray(response.data) ? response.data : [];
      const transformedMessages = messages.map((msg) => 
        transformSocketEventToMessage(msg, params.currentUserKey, params.menuProps)
      );

      // Extract feedId from the first message (all messages in context belong to the same feed)
      const feedId = messages.length > 0 ? (messages[0].feedId || messages[0].feed_id) : null;

      return {
        data: transformedMessages,
        feedId: feedId,
      };
    }

    return {
      data: [],
      feedId: null,
    };
  } catch (error) {
    console.error('[FeedService] Failed to get message context:', error);
    return {
      data: [],
      feedId: null,
    };
  }
};

/**
 * Create a message in a feed
 */
export const createMessage = async (feedId, messageData) => {
  try {
    const payload = {
      messageType: messageData.messageType || MESSAGE_TYPE.TEXT,
      body: messageData.content || messageData.body,
      isPinned: messageData.isPinned || false,
      attachmentUrls: messageData.attachments || [],
    };

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/${feedId}/messages?noLoader=true`,
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response;
  } catch (error) {
    console.error('Failed to create message:', error);
    throw error;
  }
};

/**
 * Get paginated DM feeds for Messages tab
 * Returns paginated DM conversation data for display in Messages tab
 * @param {Object} params - Query parameters including pagination
 * @returns {Object} Paginated response with DM feeds data
 */
export const getDMFeeds = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      feedType: FEED_TYPE.DM,
      page: (params.page || 1) - 1, // Convert to 0-based pagination
      size: params.size || PAGINATION_CONSTANTS.DEFAULT_DM_PAGE_SIZE,
      searchTerm: params.searchTerm || '',
      ...(params.locationKeys && { locationKeys: params.locationKeys.join(',') }),
      ...(params.groupKeys && { groupKeys: params.groupKeys.join(',') }),
    });

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      const pageData = response.data;

      // Handle paginated response structure
      return {
        data: {
          content: pageData.content || [],
          totalElements: pageData.totalElements || 0,
          totalPages: pageData.totalPages || 0,
          currentPage: params.page || 1, // Return 1-based page for UI
          hasMore: !pageData.last,
        },
      };
    }

    return {
      data: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: params.page || 1,
        hasMore: false,
      },
    };
  } catch (error) {
    console.error('[FeedService] Failed to get DM feeds:', error);
    throw error;
  }
};

/**
 * Create or get DM feed
 */
export const createDMFeed = async (participantPersonKeys) => {
  try {
    const payload = {
      feedType: FEED_TYPE.DM,
      name: 'Direct Message',
      participantPersonKeys: participantPersonKeys,
      isConfidential: false,
    };

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}?noLoader=true`,
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (response.status === 201 || response.status === 200) {
      return transformApiFeedToDisplay(response.data, null, 'dm');
    }

    throw new Error('Failed to create DM feed');
  } catch (error) {
    console.error('Failed to create DM feed:', error);
    throw error;
  }
};

/**
 * Get paginated announcement feeds for Announcements tab
 * Returns paginated announcement data for display in Announcements tab
 * @param {Object} params - Query parameters including pagination and scope
 * @returns {Object} Paginated response with announcement feeds data
 */
export const getAnnouncementFeeds = async (params = {}) => {
  try {
    // Use the type directly as it's already mapped in the hook
    const filter = params.type || 'ALL_ANNOUNCEMENTS';
    const queryParams = new URLSearchParams({
      filter,
      page: (params.page || 1) - 1, // Convert to 0-based pagination
      size: params.size || PAGINATION_CONSTANTS.DEFAULT_ANNOUNCEMENT_PAGE_SIZE,
    });

    const url = `${HERMES_BASE_URL}/api/feeds/announcements?noLoader=true&${queryParams}`;

    let response = null;
    if (filter === 'ALL_ANNOUNCEMENTS') {
      response = await getAnnouncementAudit({
        isDeleted: false,
        ...params
      });
    } else {
      response = await request({
        url,
        method: 'GET',
      });
    }


    if (response.status === 200 && response.data) {
      const pageData = response.data;

      // Handle paginated response structure
      return {
        data: {
          content: pageData.content || [],
          totalElements: pageData.totalElements || 0,
          totalPages: pageData.totalPages || 0,
          currentPage: params.page || 1, // Return 1-based page for UI
          hasMore: !pageData.last,
        },
      };
    }

    return {
      data: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: params.page || 1,
        hasMore: false,
      },
    };
  } catch (error) {
    console.error('[FeedService] Failed to get announcement feeds:', error);
    throw error;
  }
};


/**
 * Mark announcements as read
 * Marks the provided announcement IDs as read
 * @param {Array<string>} announcementIds - Announcement identifiers to mark as read
 */
export const markAnnouncementsAsRead = async (announcementIds = []) => {
  const ids = Array.isArray(announcementIds) ? announcementIds.filter(Boolean) : [];

  if (ids.length === 0) {
    return;
  }

  try {
    await request({
      url: `${HERMES_BASE_URL}${API_BASE}/announcements/mark-read?noLoader=true`,
      method: 'PUT',
      body: JSON.stringify({ announcementIds: ids }),
    });
  } catch (error) {
    console.error('[FeedService] Failed to mark announcements as read:', error);
    throw error;
  }
};

/**
 * Get feed by id
 */
export const getFeedById = async (feedId) => {
  try {
    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/${feedId}?noLoader=true`,
      method: 'GET',
    });
    if (response.status === 201 || response.status === 200) {
      return response;
    } else {
      throw new Error('Failed to get feed by id');
    }
  } catch (error) {
    console.error('Failed to get feed by id:', error);
    throw error;
  }
};

/**
 * Search feeds
 */
export const searchFeeds = async (query, feedType = null) => {
  try {
    const queryParams = new URLSearchParams({
      q: query,
      ...(feedType && { feedType: feedType }),
    });

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/search?noLoader=true&${queryParams}`,
      method: 'GET',
    });

    if (response.status === 200) {
      return (response.data || []).map((feed) => transformApiFeedToDisplay(feed, null, 'all'));
    }

    return [];
  } catch (error) {
    console.error('Failed to search feeds:', error);
    return [];
  }
};

/**
 * Get unread counts for feeds
 * Fetches the unread message counts for all feeds and returns categorized counts
 * @returns {Object} Object with unreadCount, unreadAnnouncementsCount, unreadMessagesCount, unreadChannelsCount
 */
export const getUnreadCounts = async () => {
  try {
    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/unread-counts?noLoader=true`,
      method: 'GET',
    });

    if (response.status === 200) {
      console.log('[FeedService] Unread counts response:', response.data);
      return mapUnreadCounts(response.data);
    }

    // If response is not 200 but no data, return zero counts
    return mapUnreadCounts([]);
  } catch (error) {
    console.error('[FeedService] Failed to get unread counts:', error);
    // Return zero counts on error
    return mapUnreadCounts([]);
  }
};

/**
 * Mark feed as read
 * Marks all messages in a feed as read
 * Returns the API response including messagesMarkedAsRead count
 * @param {string} feedId - The feed ID to mark as read
 * @returns {Promise<Object>} Response containing messagesMarkedAsRead count
 */
export const markFeedAsRead = async (feedId) => {
  if (!feedId) {
    console.warn('[FeedService] Cannot mark feed as read: feedId is missing');
    return { data: { messagesMarkedAsRead: 0 } };
  }

  try {
    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/${feedId}/mark-as-read?noLoader=true`,
      method: 'POST',
    });
    
    return response;
  } catch (error) {
    console.error('[FeedService] Failed to mark feed as read:', error);
    return { data: { messagesMarkedAsRead: 0 } };
  }
};

/**
 * Get read receipts for a message
 * Fetches the read receipts (who read the message) for a specific message
 * @param {string} messageId - The message ID to get read receipts for
 * @returns {Promise<Object>} Response containing read receipts data
 */
export const getReadReceipts = async (messageId) => {
  try {
    if (!messageId) {
      console.warn('[FeedService] Cannot get read receipts: messageId is missing');
      return { data: [] };
    }

    const response = await request({
      url: `${HERMES_BASE_URL}${API_BASE}/read-receipts/${messageId}?noLoader=true`,
      method: 'GET',
    });

    if (response.status === 200 && response.data) {
      return response;
    }

    return { data: [] };
  } catch (error) {
    console.error('[FeedService] Failed to get read receipts:', error);
    return { data: [] };
  }
};

/**
 * Join socket rooms for feeds
 */
export const joinFeedRoom = (feedIds) => {
  joinMeetingRoom(feedIds);
};

export const getUserDataFromType = (listOfUsers = [], type, propertyToBeSearchedOn) => {
  return listOfUsers
    .filter(user => user[propertyToBeSearchedOn] === type);
}


export const handleToggleModal = (modalRef) => {
  if (modalRef.current) {
    modalRef.current.toggleModal();
  }
};

const getStringFromProprety = (property) => {
  if (property) {
    return property.trim();
  }
  return '';
};

const getFacilityName = (locationHistory) => {
  if (locationHistory.length > 1) {
    locationHistory.sort((a, b) => getStringFromProprety(a.facilityName).localeCompare(getStringFromProprety(b.facilityName)));
    return `${locationHistory[0].jurisdictionName}, ${locationHistory[0].facilityName} (+${locationHistory.length - 1})`;
  }
  return `${locationHistory[0].jurisdictionName}, ${locationHistory[0].facilityName}`;
}

export const deduplicateSearchMembersAndGroupLocationsHistory = (searchMembers) => {
    const deduplicatedSearchMembers = [];
    searchMembers.data.forEach(item => {
      if (item.recordType != 'person') { // if not a person, add to deduplicatedSearchMembers
        deduplicatedSearchMembers.push(item);
        return;
      }
      if (item.recordType == 'person' && !deduplicatedSearchMembers.some(m => m.id === item.id)) {
        const locationHistory = searchMembers.data.filter(_item => _item.id === item.id).map(_item => ({
          jurisdictionName: _item.jurisdictionName,
          facilityName: _item.facilityName,
          housingUnitName: _item.housingUnitName,
        }));
        if (item.roleName === 'Learner') {
          item.facilityName = item.housingUnitName || item.facilityName;
        } else if (item.roleName === 'Attorney') {
          // Preserve Attorney roleName, don't change it to Staff
          item.facilityName = getFacilityName(locationHistory);
        } else {
          item.roleName = 'Staff';
          item.facilityName = getFacilityName(locationHistory);
        }
        deduplicatedSearchMembers.push(item);
      }
    });
    return deduplicatedSearchMembers;
};

/**
 * Fetch attorney messaging feature status
 * Checks if the messagingAttorney feature is enabled for the active location
 * @returns {Promise<boolean>} True if attorney messaging is enabled, false otherwise
 */
export const fetchAttorneyMessagingStatus = async () => {
  try {
    const activeLocationId = getActiveLocationId();
    if (activeLocationId) {
      const response = await getFeatures(activeLocationId);
      if (response?.statusCode === 200 && response?.locationFeatures) {
        const managementService = new ManagementService();
        // Find the messagingAttorney feature in the locationFeatures array
        const messagingAttorneyFeature = response.locationFeatures.find(
          (feature) => feature.featureName === managementService.MessagingAttorney
        );
        return messagingAttorneyFeature?.status === true;
      }
    }
    return false;
  } catch (error) {
    console.error('[FeedService] Failed to fetch attorney messaging status:', error);
    return false;
  }
};